import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LABELS } from "@/constants/testIds";
import HintIcon from "@/components/HintIcon";
import { toast } from "sonner";
import { Tag, Plus, Save, Trash2, Info, X, Bot } from "lucide-react";

const COLORS = ["slate", "indigo", "orange", "red", "emerald", "purple"];

const COLOR_MAP = {
  slate: "bg-slate-50 text-slate-700 border-slate-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  red: "bg-red-50 text-red-700 border-red-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  purple: "bg-purple-50 text-purple-700 border-purple-200",
};

function slugify(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

export default function Labels() {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, slug, name, description, color, ai_managed}
  const [confirmingDelete, setConfirmingDelete] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/labels");
      setLabels(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ slug: "", name: "", description: "", color: "indigo", ai_managed: true, score_weight: 0 });
  const openEdit = (l) => setEditing({ ...l, score_weight: l.score_weight ?? 0 });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    const payload = {
      id: editing.id,
      slug: (editing.slug || slugify(editing.name)).trim(),
      name: editing.name.trim(),
      description: editing.description || "",
      color: editing.color,
      ai_managed: !!editing.ai_managed,
      score_weight: Math.max(-30, Math.min(30, parseInt(editing.score_weight ?? 0, 10) || 0)),
    };
    if (!payload.slug) return toast.error("Slug is required");
    try {
      await api.post("/labels", payload);
      toast.success(editing.id ? "Label updated" : "Label created");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (l) => {
    // Two-click confirm — first click arms, second within 5s deletes.
    // Native confirm() is silently blocked by the preview iframe.
    if (confirmingDelete !== l.id) {
      setConfirmingDelete(l.id);
      setTimeout(() => setConfirmingDelete((cur) => (cur === l.id ? "" : cur)), 5000);
      return;
    }
    setConfirmingDelete("");
    try {
      await api.delete(`/labels/${l.id}`);
      toast.success("Label deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid={LABELS.root} className="p-8 lg:p-12 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">AI Configuration</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            Labels
            <HintIcon
              testId="labels-page-hint"
              hint={
                <>
                  The master list of tags that can be applied to a user — manually by agents or automatically by the AI
                  based on conversation signals.<br /><br />
                  Each label carries a <b>description</b> (fed to the AI so it knows when to apply it) and a
                  <b> score weight</b> (adds/subtracts from the conversion score when the label is applied — e.g.,
                  <b> Hot Lead</b> = +40, <b>Not Interested</b> = -30).<br /><br />
                  Use <b>Statistics → Label Distribution</b> to see counts and click through to filtered user lists.
                </>
              }
            />
          </h1>
        </div>
        <button
          data-testid={LABELS.addBtn}
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150"
        >
          <Plus className="h-3.5 w-3.5" /> New label
        </button>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-6 flex gap-2 items-start">
        <Info className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
        <div className="text-xs text-indigo-900 leading-relaxed">
          <b>How labels work:</b> Labels power segmentation, personalised AI drafts, and reporting.
          Marking a label as <b>AI-managed</b> allows the AI to auto-apply it when the user's messages match the description.
          Non-AI-managed labels are only applied manually by agents.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Label</th>
              <th className="text-left px-4 py-3 font-semibold">Slug</th>
              <th className="text-left px-4 py-3 font-semibold">Description (also read by AI)</th>
              <th className="text-left px-4 py-3 font-semibold" title="Points added to Conversion Score when this label is applied">Score Δ</th>
              <th className="text-left px-4 py-3 font-semibold">AI-managed</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">Loading…</td></tr>}
            {!loading && labels.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">No labels yet. Click "New label" to add one.</td></tr>
            )}
            {labels.map((l) => (
              <tr key={l.id} data-testid={`${LABELS.rowPrefix}${l.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150">
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${COLOR_MAP[l.color] || COLOR_MAP.indigo}`}>
                    <Tag className="h-3 w-3" /> {l.name}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{l.slug}</td>
                <td className="px-4 py-3 text-slate-700 text-xs max-w-md">{l.description || "—"}</td>
                <td className="px-4 py-3">
                  {(() => {
                    const w = l.score_weight ?? 0;
                    if (w > 0) return <span className="font-mono font-semibold text-emerald-700">+{w}</span>;
                    if (w < 0) return <span className="font-mono font-semibold text-red-600">{w}</span>;
                    return <span className="font-mono text-slate-400">0</span>;
                  })()}
                </td>
                <td className="px-4 py-3">
                  {l.ai_managed ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                      <Bot className="h-3 w-3" /> Yes
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Manual only</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(l)} className="text-xs font-semibold text-slate-700 hover:text-orange-600 mr-3">Edit</button>
                  <button
                    onClick={() => remove(l)}
                    className={`text-xs font-semibold inline-flex items-center gap-1 transition-colors duration-150 ${confirmingDelete === l.id ? "text-white bg-red-600 px-2 py-0.5 rounded" : "text-red-600 hover:text-red-800"}`}
                    title={confirmingDelete === l.id ? "Click again to confirm — removes label from all users carrying it" : `Delete "${l.name}"`}
                  >
                    <Trash2 className="h-3 w-3" /> {confirmingDelete === l.id ? "Click to confirm" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">
                {editing.id ? "Edit label" : "New label"}
              </h3>
              <button onClick={() => setEditing(null)}><X className="h-4 w-4 text-slate-500" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Display name</label>
                <input
                  data-testid={LABELS.nameInput}
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g., Hot Lead"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Slug <span className="text-slate-500 font-normal">(unique id — auto-derived from name)</span>
                </label>
                <input
                  data-testid={LABELS.slugInput}
                  value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                  placeholder="e.g., hot_lead"
                  disabled={!!editing.id}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150 font-mono disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Description <span className="text-slate-500 font-normal">(fed to AI so it knows when to apply this)</span>
                </label>
                <textarea
                  data-testid={LABELS.descInput}
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="e.g., User has expressed clear buying intent — asked about pricing, plans, or when to subscribe."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Color</label>
                  <select
                    data-testid={LABELS.colorSelect}
                    value={editing.color}
                    onChange={(e) => setEditing({ ...editing, color: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
                  >
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">AI can auto-apply</label>
                  <button
                    data-testid={LABELS.aiManagedToggle}
                    onClick={() => setEditing({ ...editing, ai_managed: !editing.ai_managed })}
                    className={`w-full px-3 py-2 text-xs font-semibold rounded-md border transition-colors duration-150 ${
                      editing.ai_managed
                        ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-500"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {editing.ai_managed ? "AI-managed (on)" : "Manual only (off)"}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Score weight <span className="text-slate-500 font-normal">(-30 to +30 — added to Conversion Score when applied)</span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="-30"
                    max="30"
                    step="1"
                    value={editing.score_weight ?? 0}
                    onChange={(e) => setEditing({ ...editing, score_weight: parseInt(e.target.value, 10) })}
                    className="flex-1 accent-orange-500"
                  />
                  <input
                    type="number"
                    min="-30"
                    max="30"
                    value={editing.score_weight ?? 0}
                    onChange={(e) => setEditing({ ...editing, score_weight: parseInt(e.target.value || "0", 10) })}
                    className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none tabular text-center font-mono"
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  Positive = boosts likelihood the user converts (e.g. Hot Lead = +15). Negative = dampens
                  (e.g. Objecting = -10). The sum of all applied label weights is capped at ±30 on the final score.
                </div>
              </div>

              <div className="pt-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Preview</div>
                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${COLOR_MAP[editing.color] || COLOR_MAP.indigo}`}>
                  <Tag className="h-3 w-3" /> {editing.name || "Label name"}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button
                data-testid={LABELS.saveBtn}
                onClick={save}
                className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150"
              >
                <Save className="h-3.5 w-3.5" /> Save label
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
